import { swapColumnOrder } from "@context-providers/table/column-preferences-utils";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { GridColumnOrderChangeParams } from "@mui/x-data-grid-pro";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";

/**
 * Custom hook that provides a handler for column order changes.
 * Tracks the change and updates preferences in the backend.
 *
 * @param tableType - The type of table (for tracking purposes)
 * @returns Handler function for column order changes
 */
export function useHandleColumnOrderChange(
  tableType: TableType
): (params: GridColumnOrderChangeParams) => void {
  const { trackEvent } = useTrackEvent();
  const { columnPreferences, visibleOrderedColumns, updateColumnPreferences } =
    useColumnPreferencesContext();

  return useCallback(
    (params: GridColumnOrderChangeParams) => {
      if (!columnPreferences) {
        return;
      }

      const { oldIndex, targetIndex } = params;

      if (oldIndex !== targetIndex) {
        const updatedPreferences = swapColumnOrder(
          columnPreferences,
          visibleOrderedColumns,
          oldIndex,
          targetIndex
        );

        // Track column order change
        // Subtract 1 from oldIndex to account for the checkbox column in the DataGrid
        // which is not part of visibleOrderedColumns
        const movedColumn = visibleOrderedColumns[oldIndex - 1];
        trackEvent({
          name: TableEvents.changeColumnOrder,
          props: {
            type: tableType,
            column: movedColumn,
            order: targetIndex,
          },
        });

        void updateColumnPreferences(updatedPreferences);
      }
    },
    [
      columnPreferences,
      visibleOrderedColumns,
      updateColumnPreferences,
      trackEvent,
      tableType,
    ]
  );
}
